import MenuItem from '@mui/material/MenuItem';
import dayjs from 'dayjs';
import { months, weekdays } from "./constants";
import { getAcisData } from "./dataAccessRoutines";

export function roundValue(value, digits) {
    return value = Math.round((parseFloat(value) + Number.EPSILON) * (10 ** digits)) / (10 ** digits);

}export function roundFixValue(value, digits) {
    return value = (roundValue(value, digits)).toFixed(digits);
}

export function formatDateRange(up) {
    return "Date range: " + up.fromDate.format("YYYY-MM-DD") + " through " + up.toDate.format("YYYY-MM-DD") + ".";
}

export function formatHourFilter(up) {
    return ((up.fromFilterHour && up.toFilterHour) && (up.fromFilterHour !== 1 || up.toFilterHour !== 24)) 
        ? ("Hours used: " + up.fromFilterHour.toString().padStart(2, "0") + ":00-" + up.toFilterHour.toString().padStart(2, "0") + ":00 (Local Standard Time).") 
        : "";
}

export function formatMonthFilter(up) {
    return (up.fromFilterMonth !== 1 || up.toFilterMonth !== 12) 
        ? ("Months used: "+ months[up.fromFilterMonth-1] + "-" + months[up.toFilterMonth-1] + ".") 
        : "";
}

export function formatDowFilter(up) {
    const dowarray = up.weekdays.sort().map((x) => weekdays[x]);
    return up.weekdays.length < 7 
      ? "Days of week used: " + dowarray.join(", ") + "."
      : "";
}

export function formatDayFilter(up) {
    const sd = [up.fromFilterMonth.toString().padStart(2, "0"), up.fromFilterDay.toString().padStart(2, "0")].join("-")
    const ed = [up.toFilterMonth.toString().padStart(2, "0"), up.toFilterDay.toString().padStart(2, "0")].join("-")
    return (sd !== "01-01" || ed !== "12-31") 
        ? "Day filter: " + sd + " through " + ed
        : "";
}

export function hasMissing(tabledata) {
    return tabledata && tabledata.some(el => (Object.keys(el).some(hd => el[hd] === "M")))
        ? "M = missing data." 
        : "";
}

export function selectOptions(choices) {
    const isObjects = choices ? typeof choices[0] === 'object' : [];
    return (
      choices.map(option => {
        return (
          <MenuItem 
            dense={true}
            sx={{ pt:0, pb:0 }}
            key={isObjects ? option.value : option} 
            value={isObjects ? option.value : option}
          >
            {isObjects ? option.label : option}
          </MenuItem>
        )
      })  
    )
}

export function valSmry(val, userParams, traceValue=0.001) {
    const operator = userParams.variable === "wx" ? "in" : userParams.operator;
    const thresh = userParams.variable === "wx"
        ? userParams.wxvariables
        : (userParams.thresh === "T" ? traceValue : parseFloat(userParams.thresh));
    var operation = {
        'le': function(a, b) { return a <= b },
        'lt': function(a, b) { return a < b },
        'gt': function(a, b) { return a > b },
        'ge': function(a, b) { return a >= b },
        'eq': function(a, b) { return a === b },
        'ne': function(a, b) { return a !== b },
        // used to determine if present weather includes anything in requested list (wxvariables)
        'in': function(a, b) { return b.some(ky => a.includes(ky))},
    };
    return operation[operator](val, thresh) ? 1 : 0
}


export function valExt(val, userParams, prevExt) {
  if (userParams.smry === 'max' && val >= prevExt) {
    return val
  } else if (userParams.smry === 'min' && val <= prevExt) {
    return val
  } else {
    return prevExt
  }
}

function checkEndGridDataRange(userParams, setGridError, setDisableGoButton, gridEndDate, setGridEndDate) {
    // end date range check for gridded dataset
    const gridkey = userParams.grid;
    if (gridkey in gridEndDate) {
        if (userParams.toDate.isAfter(gridEndDate[gridkey], 'date')) {   //'date' is resolution of comparison
            setGridError('This grid not available after ' + gridEndDate[gridkey] + '.');
            setDisableGoButton(true);
        }        
    } else {
        // get data for a single grid point for the last month and find last day with non-missing data
        const now = dayjs();
        const start = now.subtract(1, 'month');
        const acisParams = {
            "loc": "-77,42",
            "sdate": start.format('YYYY-MM-DD'),
            "edate": now.format('YYYY-MM-DD'),
            "grid": userParams.grid,
            "elems": [{"name":"pcpn","interval":[0,0,1],"duration":1}]
        }
        getAcisData(acisParams)
            .then(res => { 
                const last = res.data.findLast((item) => item[1] !== -999 ); 
                setGridEndDate({...gridEndDate, [gridkey]: last[0]})
                if (userParams.toDate.isAfter(last[0], 'date')) {   //'date' is resolution of comparison
                    setGridError('This grid not available after ' + last[0] + '.');
                    setDisableGoButton(true);
                }
                })
            .catch(error =>  { 
                console.log("Data retrieval error: " + JSON.stringify(error)); 
                setGridError("Error determining available date range.")
                })
    }
}

// determine if the specified grid is available for the area/variable/dates specified
export function checkGridAvailable(userParams, setGridError, setDisableGoButton, gridEndDate, setGridEndDate) {
    // clear existing status
    setGridError("");
    setDisableGoButton(false);
    // geographic checks
    const newGrid = userParams.grid;
    if (newGrid === 'nrcc-model') {
        // only available east of the Rockies
        if (userParams.areaType === 'bbox') {
            const coors = userParams.area.split(",");
            if (parseFloat(coors[0]) < -104) {
                setGridError('This grid not available west of 104°W.');     //partially unavailable
                setDisableGoButton(parseFloat(coors[2]) < -104);            //totally unavailable
            }
        } else if (userParams.areaType === 'state' || userParams.areaType === 'region') {
            const westernStates = ['WA', 'OR', 'CA', 'NV', 'AZ', 'MT','ID', 'UT', 'WY'];
            const states = userParams.area.split(",");
            const found = states.some(r => westernStates.includes(r.toUpperCase()));
            if (found) {
                const every = states.every(r => westernStates.includes(r.toUpperCase()));
                setGridError('This grid only available east of Rockies.');      //partially unavailable
                setDisableGoButton(every);                                      //totally unavailable          
            }
        }
    }
    // start date range checks
    if (newGrid === 'nrcc-nn' && userParams.fromDate.year() < 1950) {
        setGridError('This grid not available prior to 1950.');
        setDisableGoButton(true);
    } else if (newGrid === 'nrcc-model') {
        const firstYear = userParams.variable === "pcpn" ? 2002 : 1979
        if (userParams.fromDate.year() < firstYear) {
            setGridError('This ' + (userParams.variable === "pcpn" ? "precipitation " : "") + 'grid not available prior to ' + firstYear + '.');
            setDisableGoButton(true);;
        }
    } else if (newGrid === 'ncei-clim' || newGrid === 'prism') {
        const wholeMonths = userParams.fromDate.date() === 1 && userParams.toDate.date() === userParams.toDate.daysInMonth();
        if (wholeMonths) {
            if (userParams.fromDate.year() < 1895) {
                setGridError('This monthly grid not available prior to 1895.');
                setDisableGoButton(true);
            } 
        } else {
            const firstYear = newGrid === 'ncei-clim' ? 1951 : 1981;
            if (userParams.fromDate.year() < firstYear) {
                setGridError('This dailly grid not available prior to ' + firstYear + '.');
                setDisableGoButton(true);    
            }
        }
    }
    // end date range check
    checkEndGridDataRange(userParams, setGridError, setDisableGoButton, gridEndDate, setGridEndDate)
}
